import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import './ClimateControl.css'

export interface ArrivalPrepCardProps {
  target: number
  min: number
  max: number
  onTargetChange: (target: number) => void
  /** Frase bajo el título; por defecto la del control de clima */
  text?: ReactNode
  /** Confirmación "Estará a 22 °C cuando llegues" con su frase; se muestra si se pasa */
  confirmation?: { text: ReactNode } | null
  /** Contenido extra al final (hora del registro y botón de guardar en Accesos) */
  children?: ReactNode
  className?: string
}

/**
 * Tarjeta "Prepara la temperatura para tu llegada" (bundle B8 §8c y §8i). Es la tarjeta interior del
 * control de clima bloqueado, exportada sin la cabecera "Clima" para el paso 6 del check-in (pendiente
 * B2 del inventario §7). Botones de 72 px, sin slider, límites en texto.
 */
export function ArrivalPrepCard({ target, min, max, onTargetChange, text, confirmation, children, className }: ArrivalPrepCardProps) {
  const { t, i18n } = useTranslation()
  const temp = `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(target)} °C`
  return (
    <div className={cx('hp-climate__card', className)}>
      <div>
        <div className="hp-climate__card-title">{t('v2.ds.climate.prepararTitulo')}</div>
        <p className="hp-climate__card-text">{text ?? t('v2.ds.climate.prepararTexto')}</p>
      </div>
      <div className="hp-climate__stepper">
        <button type="button" aria-label={t('v2.ds.climate.bajarGrado')} disabled={target <= min} onClick={() => onTargetChange(target - 1)} className="hp-climate__stepbtn hp-climate__stepbtn--small">
          <Icon name="minus" size={28} />
        </button>
        <div className="hp-climate__prep">
          <div className="hp-climate__prep-value tnum" aria-live="polite">
            {temp}
          </div>
          <div className="hp-climate__caption">{t('v2.ds.climate.alLlegar')}</div>
        </div>
        <button type="button" aria-label={t('v2.ds.climate.subirGrado')} disabled={target >= max} onClick={() => onTargetChange(target + 1)} className="hp-climate__stepbtn hp-climate__stepbtn--small">
          <Icon name="plus" size={28} />
        </button>
      </div>
      <p className="hp-climate__card-text">{t('v2.ds.climate.rango', { min, max })}</p>
      <div aria-live="polite">
        {confirmation && (
          <div className="hp-climate__status hp-climate__status--success">
            <Icon name="check-circle" size={22} />
            <div>
              <div className="hp-climate__status-title">{t('v2.ds.climate.estaraA', { temp })}</div>
              <div className="hp-climate__status-text">{confirmation.text}</div>
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
  )
}
